import React, { useEffect, useRef } from 'react';
interface FeatureProps {
  emoji: string;
  title: string;
  description: string;
  index: number;
}
function Feature({
  emoji,
  title,
  description,
  index
}: FeatureProps) {
  const featureRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
        }
      });
    }, {
      threshold: 0.1
    });
    if (featureRef.current) {
      observer.observe(featureRef.current);
    }
    return () => {
      if (featureRef.current) {
        observer.unobserve(featureRef.current);
      }
    };
  }, []);
  return <div ref={featureRef} className={`opacity-0 translate-y-10 transition-all duration-700 bg-white dark:bg-charcoal-800 border border-charcoal-100 dark:border-charcoal-700 rounded-3xl p-8 hover:border-coral-500/30 hover:shadow-lg transition-all duration-300`} style={{
    transitionDelay: `${index * 100}ms`
  }} data-id="element-75">
      <div className="text-5xl mb-6" data-id="element-76">{emoji}</div>
      <h3 className="text-2xl font-bold mb-4 text-charcoal-500 dark:text-white" data-id="element-77">
        {title}
      </h3>
      <p className="text-charcoal-400 dark:text-charcoal-300 leading-relaxed text-lg" data-id="element-78">
        {description}
      </p>
    </div>;
}
export function Features() {
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100');
          entry.target.classList.remove('opacity-0');
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <div id="features" ref={sectionRef} className="py-32 md:py-40 opacity-0 transition-opacity duration-1000 bg-cream-100 dark:bg-charcoal-900" data-id="element-79">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-80">
        <h2 className="text-4xl md:text-5xl font-bold mb-20 text-center text-charcoal-500 dark:text-white" data-id="element-81">
          Your AI nutrition sidekick
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6" data-id="element-82">
          <Feature index={0} emoji="💬" title="Talk like a human" description='"Find me high-protein lunch under 600 cals" — No buttons. No forms. Just ask.' data-id="element-83" />
          <Feature index={1} emoji="🎯" title="Know before you order" description="Precise protein, carbs, fats for every meal. No more guessing games." data-id="element-84" />
          <Feature index={2} emoji="📍" title="See what's close" description="Macro-friendly spots near you with distance & walk time. Find it. Order it. Done." data-id="element-85" />
          <Feature index={3} emoji="🧠" title="Gets smarter with use" description="Remembers what you love and hate. Better recommendations every time." data-id="element-86" />
          <Feature index={4} emoji="⚡" title="Under 30 seconds" description="Ask. Get results. Order with confidence. No spreadsheets. No headaches." data-id="element-87" />
        </div>
      </div>
    </div>;
}